import {
    Button,
    InputControl,
    Loading,
    Picker,
    useAuth,
} from "@venuepos/react-common";
import {
    GQViWarehouse,
    useSetDepartment2WarehouseMutation,
    useViWarehousesQuery,
} from "graphql-sdk";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHandleMutationError } from "../../../hooks";

export function ViWarehouseForm({
    departmentId,
    warehouse: warehouse,
}: {
    departmentId: string;
    warehouse: GQViWarehouse;
}) {
    const [t] = useTranslation();
    const [setDepartment2Warehouse] = useSetDepartment2WarehouseMutation();
    const { handleMutationError } = useHandleMutationError();
    const auth = useAuth();

    // For now we only have one warehouse pr department, but the endpoints support multiple warehouses
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<
        string | undefined
    >(warehouse?.id);

    const { data, loading } = useViWarehousesQuery({
        variables: {
            pagination: {
                pageSize: 99999,
                sort: "name",
                sortDirection: "ASC",
            },
        },
        fetchPolicy: "no-cache",
    });

    const updateWarehouseRelation = useCallback(async () => {
        const warehouseId =
            selectedWarehouseId === "" ? undefined : selectedWarehouseId;

        await handleMutationError(
            async () =>
                await setDepartment2Warehouse({
                    variables: {
                        departmentId: departmentId,
                        warehouseId: warehouseId,
                    },
                }),
            t("backoffice.vi.warehouse_saved", "Warehouse saved")
        );
    }, [
        handleMutationError,
        t,
        setDepartment2Warehouse,
        departmentId,
        selectedWarehouseId,
    ]);

    if (!auth.may("merchant.vi")) {
        return null;
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <InputControl>
                <Picker
                    label={t(
                        "backoffice.vi.select_warehouse",
                        "Select warehouse"
                    )}
                    selectedValue={selectedWarehouseId}
                    onValueChange={value => {
                        setSelectedWarehouseId(value);
                    }}
                >
                    <Picker.Item
                        label={t("backoffice.vi.no_warehouse", "No warehouse")}
                        value=""
                    />
                    {data.viWarehouses?.data.map(itr => {
                        return (
                            <Picker.Item
                                key={`warehouse_${itr.id}`}
                                label={itr.name}
                                value={itr.id}
                            />
                        );
                    })}
                </Picker>
            </InputControl>

            <Button
                onPress={() => {
                    updateWarehouseRelation();
                }}
            >
                {t("common.save", "Save")}
            </Button>
        </>
    );
}
